import React, { useEffect, useState } from "react";
import { AlertTriangle, MapPin, Phone, Check, RefreshCw } from "lucide-react";
import { api } from "../api/client";

const TYPE_LABELS = {
  accident: { label: "Accidente", color: "bg-red-100 text-red-700 border-red-300" },
  robbery: { label: "Robo/Asalto", color: "bg-red-100 text-red-700 border-red-300" },
  mechanical: { label: "Falla Mecánica", color: "bg-amber-100 text-amber-700 border-amber-300" },
  medical: { label: "Médica", color: "bg-purple-100 text-purple-700 border-purple-300" },
  other: { label: "Otra", color: "bg-neutral-100 text-neutral-700 border-neutral-300" },
};

export default function SOSView() {
  const [alerts, setAlerts] = useState([]);
  const [filter, setFilter] = useState("open"); // open | resolved | all
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const q = filter === "all" ? "" : `?status=${filter}`;
      const { data } = await api.get(`/sos/alerts${q}`);
      setAlerts(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [filter]);

  const resolve = async (id) => {
    if (!window.confirm("¿Marcar esta alerta como resuelta?")) return;
    try { await api.post(`/sos/alerts/${id}/resolve`); load(); } catch (e) { alert("Error"); }
  };

  return (
    <div className="space-y-4 fade-in" data-testid="sos-view">
      <div className="flex justify-between items-center">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-red-600">Emergencia</div>
          <h1 className="font-display font-bold text-2xl text-neutral-900 mt-0.5 flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-red-500" /> Alertas SOS
          </h1>
        </div>
        <div className="flex gap-2 items-center">
          <div className="flex gap-1 bg-neutral-100 p-1 rounded">
            {["open", "resolved", "all"].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1 text-xs font-semibold rounded ${filter === f ? "bg-white shadow-sm text-[#1A237E]" : "text-neutral-500"}`}
                data-testid={`sos-filter-${f}`}
              >
                {f === "open" ? "Abiertas" : f === "resolved" ? "Resueltas" : "Todas"}
              </button>
            ))}
          </div>
          <button onClick={load} className="p-2 rounded hover:bg-neutral-100" data-testid="sos-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {alerts.length === 0 ? (
        <div className="office-card p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
            <Check className="w-8 h-8 text-emerald-500" />
          </div>
          <div className="font-semibold text-neutral-800">Sin alertas {filter === "open" ? "activas" : ""}</div>
          <div className="text-sm text-neutral-500 mt-1">Los conductores están seguros 🚕</div>
        </div>
      ) : (
        <div className="space-y-3">
          {alerts.map(a => {
            const t = TYPE_LABELS[a.type] || TYPE_LABELS.other;
            return (
              <div key={a.id} className={`office-card p-4 border-l-4 ${a.status === "open" ? "border-red-500" : "border-emerald-500"}`} data-testid={`sos-alert-${a.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${a.status === "open" ? "bg-red-100" : "bg-emerald-100"}`}>
                    <AlertTriangle className={`w-5 h-5 ${a.status === "open" ? "text-red-600" : "text-emerald-600"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${t.color}`}>{t.label}</span>
                      <span className={`text-[10px] font-bold uppercase ${a.status === "open" ? "text-red-600" : "text-emerald-600"}`}>
                        {a.status === "open" ? "ABIERTA" : "RESUELTA"}
                      </span>
                      <span className="text-xs text-neutral-500 ml-auto">{new Date(a.created_at).toLocaleString("es-PE")}</span>
                    </div>
                    <div className="mt-2 font-semibold text-neutral-800">{a.driver_name}</div>
                    <div className="flex items-center gap-1.5 text-xs text-neutral-500 mt-1">
                      <Phone className="w-3 h-3" /> {a.driver_phone || "—"}
                    </div>
                    {a.notes && <div className="text-sm text-neutral-600 mt-2 p-2 bg-neutral-50 rounded">{a.notes}</div>}
                    {a.lat && a.lng && (
                      <a
                        href={`https://maps.google.com/?q=${a.lat},${a.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 mt-2 text-sm text-[#3949AB] hover:underline"
                        data-testid={`sos-map-${a.id}`}
                      >
                        <MapPin className="w-4 h-4" />
                        Ver ubicación en el mapa ({a.lat.toFixed(5)}, {a.lng.toFixed(5)})
                      </a>
                    )}
                  </div>
                  {a.status === "open" && (
                    <button
                      onClick={() => resolve(a.id)}
                      className="btn-primary !text-xs !py-1.5 !px-3"
                      data-testid={`sos-resolve-${a.id}`}
                    >
                      <Check className="w-3.5 h-3.5" /> Resolver
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
